/**
 * EventSandbox — Two-World event binding implementation.
 *
 * SandboxWindow wraps an internal EventTarget. App code calling
 * sandboxWindow.addEventListener() registers on the internal EventTarget ONLY —
 * the real window is never touched. Studio's tinykeys shortcuts are unaffected.
 *
 * Phase 152 — implements EventSandbox interface from event-sandbox.types.ts
 */

import type {
  EventSandbox,
  EventSandboxOptions,
  CreateEventSandbox,
  SandboxWindow,
} from './event-sandbox.types.js'

const DEFAULT_FORWARDED_TYPES = [
  'keydown',
  'keyup',
  'keypress',
  'click',
  'input',
  'change',
  'focus',
  'blur',
]

/**
 * Create a capture-phase forwarder that forwards events originating inside
 * containerEl to the internal EventTarget.
 *
 * Note: We cannot re-dispatch the original event object (it's already being
 * dispatched and would throw InvalidStateError). Instead we create a new event
 * of the same type so app handlers receive a fresh event object.
 */
function makeForwarder(
  target: EventTarget,
  forwardedTypes: string[],
  containerEl: HTMLElement
): (event: Event) => void {
  return (event: Event) => {
    if (!forwardedTypes.includes(event.type)) return
    if (!containerEl.contains(event.target as Node)) return
    // Clone the event so we can dispatch it on a different target.
    // structuredClone doesn't work for Event; use constructor cloning.
    try {
      const cloned = new (event.constructor as typeof Event)(event.type, event)
      target.dispatchEvent(cloned)
    } catch {
      // Fallback: dispatch a generic Event if constructor cloning fails
      target.dispatchEvent(new Event(event.type, event))
    }
  }
}

export const createEventSandbox: CreateEventSandbox = (
  options: EventSandboxOptions
): EventSandbox => {
  // Internal state
  let internalTarget: EventTarget | null = new EventTarget()
  let containerEl: HTMLElement | null = null
  let captureListener: ((e: Event) => void) | null = null
  let destroyed = false
  const listenerCountMap = new Map<string, number>()

  const forwardedEventTypes: string[] =
    options.forwardedEventTypes ?? DEFAULT_FORWARDED_TYPES

  // Build the SandboxWindow object
  const sandboxWindow: SandboxWindow = {
    addEventListener(
      type: string,
      listener: EventListenerOrEventListenerObject,
      opts?: boolean | AddEventListenerOptions
    ): void {
      if (destroyed) return
      internalTarget!.addEventListener(type, listener, opts)
      const count = (listenerCountMap.get(type) ?? 0) + 1
      listenerCountMap.set(type, count)
      options.onListenerRegistered?.(type, count)
    },

    removeEventListener(
      type: string,
      listener: EventListenerOrEventListenerObject,
      opts?: boolean | EventListenerOptions
    ): void {
      if (destroyed) return
      internalTarget!.removeEventListener(type, listener, opts)
      const count = Math.max(0, (listenerCountMap.get(type) ?? 1) - 1)
      listenerCountMap.set(type, count)
    },

    dispatchEvent(event: Event): boolean {
      return internalTarget?.dispatchEvent(event) ?? false
    },

    get __cogo_tools__(): Record<string, unknown> | undefined {
      return (window as any).__cogo_app_tools__?.[options.appId]
    },

    set __cogo_tools__(value: Record<string, unknown> | undefined) {
      const existing = (window as any).__cogo_app_tools__ ?? {}
      ;(window as any).__cogo_app_tools__ = { ...existing, [options.appId]: value }
    },

    get innerWidth(): number {
      return typeof window !== 'undefined' ? window.innerWidth : 0
    },

    get innerHeight(): number {
      return typeof window !== 'undefined' ? window.innerHeight : 0
    },

    get devicePixelRatio(): number {
      return typeof window !== 'undefined' ? window.devicePixelRatio : 1
    },
  }

  const eventSandbox: EventSandbox = {
    get sandboxWindow(): SandboxWindow {
      return sandboxWindow
    },

    mount(el: HTMLElement): void {
      if (destroyed) return

      // Remove old capture listener if remounting
      if (captureListener && containerEl) {
        for (const type of forwardedEventTypes) {
          containerEl.removeEventListener(type, captureListener, { capture: true })
        }
      }

      containerEl = el
      captureListener = makeForwarder(internalTarget!, forwardedEventTypes, el)

      for (const type of forwardedEventTypes) {
        containerEl.addEventListener(type, captureListener, { capture: true })
      }
    },

    unmount(): void {
      if (!captureListener || !containerEl) return

      for (const type of forwardedEventTypes) {
        containerEl.removeEventListener(type, captureListener, { capture: true })
      }

      captureListener = null
      containerEl = null
    },

    dispatchToSandbox(event: Event): void {
      if (destroyed) return
      internalTarget!.dispatchEvent(event)
    },

    destroy(): void {
      // Remove capture listener if mounted
      eventSandbox.unmount()

      // Dereference internal EventTarget (all handlers GC-eligible)
      internalTarget = null
      destroyed = true
      listenerCountMap.clear()
    },
  }

  return eventSandbox
}
